import { ApiClient } from './client'
import type { OfflineWireBatch } from '@/offline/capture/wire'
import type { DescriptorSelection, ScorePayload } from '@/offline/capture/types'
import type { SessionCaptureScoreRecord } from './sessionCapture'
import type { FocusAssignments } from './assessmentFocus'
import type { TryoutContextResponse } from './tryoutContext'

export interface OfflineRecordKey {
  eventId: string; participantId: string; staffId: string; criterionId: string
  sessionId: string; activityId: string; episodeId: string
}
export interface OfflineCompletionScope {
  assessorCount: number; actorHasEntry: boolean; criterionIds: string[]; hasScoreElements?: boolean
}
export interface OfflineCompletion {
  version: 1; criterionCount: number; minSelectorsForConfidence: number; minDataForConfidence: number; sessionHasRows: boolean
  participants: Array<{participantId: string; overall: OfflineCompletionScope; session: OfflineCompletionScope; legacySession?: OfflineCompletionScope; activityObservations: Record<string,number>}>
  activityMaxObservations: Record<string,number>
}
export interface OfflineCaptureSnapshot {
  scorePayloadVersion?: 2
  snapshotId: string; actorId: string; eventId: string; sessionId: string; dataEpoch: string
  configurationRevision: string; preparedAt: string; sessionEndsAt: string; uploadUntil: string
  counts: Record<string, number>; snapshotHash: string
  resources: {
    scoringPolicy?: import('./tryoutFeatures').TryoutScoringPolicy
    completion?: OfflineCompletion
    session: { id: string; title: string; startsAt: string | null; endsAt: string }
    participants: Array<{ id: string; participantNumber?: string | number | null; firstName: string; preferredName: string; lastName: string }>
    criteria: Array<{ id: string; title: string; description: string; furtherInfo: string; type: string; scoringMode: 'simple' | 'feedback'; scoringAggregation: string; levels: Array<{ value: number; label: string; description?: string }>; order: number; counterTypes?: Array<Record<string, unknown>> }>
    activities: Array<{ id: string; sessionId: string; title: string; order: number }>
    activityCriteria: Array<{ activityId: string; criterionId: string; criterionBindingId?: string; categoryFocusIds?: string[] | null }>
    descriptorCategories: Array<{ id: string; criterionId: string; name: string; order: number; hidden?: boolean; sourceCategoryId?: string | null }>
    descriptorItems: Array<{ id: string; criterionId: string; categoryId: string; label: string; levelValue: number; order: number }>
    assignments: Array<{ membershipMode?: 'fixed' | 'space'; spaceId?: string | null; spaceMembers?: import('./assessmentFocus').SpaceAssignmentMember[]; id: string; title: string; instruction: string; sessionId: string; activityId: string | null; version: number; breadthPercent: number; participantIds: string[]; criterionIds: string[] }>
    scores: Array<{ id: string; recordKey: OfflineRecordKey; score: number; globalScore?: number | null; sourceScoringMode?: 'basic' | 'advanced'; effectiveScore: number | null; scoringMode: string; feedbackSelections: DescriptorSelection[]; counter?: number; buckets?: Record<string, number>; observations?: number; revision: string; present: true; updatedAt: string | null }>
    tombstones: Array<{ recordKey: OfflineRecordKey; present: false; revision: string }>
  }
}
export interface OfflineSyncResult {
  outcome: 'applied' | 'alreadyApplied' | 'conflict' | 'rejected' | 'dependencyPending'
  clientMutationId: string; originalOutcome?: 'applied' | 'conflict' | 'rejected'; code?: string
  serverReceiptId?: string; payloadHash?: string; recordRevision?: string | null
  serverReceivedAt?: string; record?: SessionCaptureScoreRecord
}
export interface OfflineSyncResponse { schemaVersion: 1; results: OfflineSyncResult[] }
export interface OfflineApiScope { clubId: string; seasonId: string; eventId: string }
export type OfflineScorePayload = ScorePayload

// Offline callers retain and lock pending work on401; a delayed old-account
// response must never log out a new session. Every dispatch binds its bearer.
const client = new ApiClient(undefined, { logoutOnUnauthorized: false })
const path = (scope: OfflineApiScope) => `/v2/season/${encodeURIComponent(scope.seasonId)}/tryouts/${encodeURIComponent(scope.eventId)}`
export const offlineCaptureAPI = {
  loadAssignments(scope: OfflineApiScope, sessionId: string, token: string, signal?: AbortSignal) {
    return client.get<FocusAssignments>(`${path(scope)}/capture/loadFocusAssignments`, { clubId: scope.clubId, sessionId, activityId: 'all' }, signal, token)
  },
  loadContext(scope: OfflineApiScope, token: string, signal?: AbortSignal) {
    return client.get<TryoutContextResponse>(`${path(scope)}/context`, { clubId: scope.clubId }, signal, token)
  },
  loadSnapshot(scope: OfflineApiScope, sessionId: string, token: string, signal?: AbortSignal) {
    return client.get<OfflineCaptureSnapshot>(`${path(scope)}/capture/loadOfflineSnapshot`, { clubId: scope.clubId, sessionId }, signal, token)
  },
  sync(scope: OfflineApiScope, batch: OfflineWireBatch, token: string, signal?: AbortSignal) {
    return client.post<OfflineSyncResponse>(`${path(scope)}/capture/sync`, batch, { clubId: scope.clubId }, signal, undefined, token)
  },
}
